import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, CreditCard, Calendar, DollarSign, Shield, Crown, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MembershipPage() {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/subscriptions/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch subscription status');
      setSubscription(await res.json());
    } catch (error) {
      console.error('Subscription fetch error:', error);
      toast.error('Failed to load subscription status');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setCheckingOut(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/subscriptions/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create checkout session');
      }
      const data = await res.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.message || 'Failed to start checkout');
      setCheckingOut(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/subscriptions/billing-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ return_url: window.location.href }),
      });
      if (res.status === 501) {
        const err = await res.json();
        toast.info(err.detail || 'Self-serve billing portal opens once production Stripe is enabled.');
        return;
      }
      if (!res.ok) throw new Error('Failed to create billing portal session');
      const data = await res.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Billing portal error:', error);
      toast.error('Failed to open billing portal');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-16 text-center text-gray-500 text-sm">
          Loading membership status...
        </div>
      </div>
    );
  }

  const hasActiveMembership = subscription?.has_membership;
  const status = subscription?.status || 'none';
  const subData = subscription?.subscription;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-10">
        <Button
          variant="ghost"
          onClick={() => navigate('/buyer/dashboard')}
          className="mb-6 text-gray-600 hover:text-gray-900 -ml-2"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Dashboard
        </Button>

        {/* Header */}
        <div className="flex items-center gap-4 mb-10">
          <div className="h-12 w-12 rounded-lg bg-[#0055FF] flex items-center justify-center flex-shrink-0">
            <Crown className="h-6 w-6 text-white" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-mono font-medium">
              Buyer Membership
            </div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-gray-900">
              LeadXchange Membership
            </h1>
          </div>
        </div>

        {/* Status card */}
        <div
          className="border border-gray-200 rounded-lg p-6 mb-6"
          data-testid="membership-status-card"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {hasActiveMembership ? (
                <>
                  <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                  <div>
                    <h2 className="font-heading text-xl font-semibold text-gray-900">
                      Active Membership
                    </h2>
                    <p className="text-sm text-gray-600">You&apos;re all set to purchase leads.</p>
                  </div>
                </>
              ) : (
                <>
                  <XCircle className="h-8 w-8 text-gray-400" />
                  <div>
                    <h2 className="font-heading text-xl font-semibold text-gray-900">
                      No Active Membership
                    </h2>
                    <p className="text-sm text-gray-600">Subscribe to start purchasing leads.</p>
                  </div>
                </>
              )}
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-gray-500 font-mono mb-1">
                Status
              </div>
              <div
                className={`text-sm font-mono font-semibold ${
                  hasActiveMembership
                    ? 'text-emerald-700'
                    : status === 'past_due'
                    ? 'text-amber-700'
                    : 'text-gray-500'
                }`}
                data-testid="membership-status-text"
              >
                {status === 'none' ? 'INACTIVE' : status.replace('_', ' ').toUpperCase()}
              </div>
            </div>
          </div>

          {subData && hasActiveMembership && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6 mt-6 border-t border-gray-100">
              <MetaItem icon={DollarSign} label="Price" value="$99 / month" />
              <MetaItem icon={Calendar} label="Billing cycle" value="Monthly" />
              <MetaItem
                icon={CreditCard}
                label="Next billing"
                value={
                  subData.current_period_end
                    ? new Date(subData.current_period_end).toLocaleDateString()
                    : '—'
                }
              />
            </div>
          )}
        </div>

        {/* Plan details */}
        <div className="border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div>
              <h3 className="font-heading text-xl font-semibold text-gray-900">
                Buyer Membership
              </h3>
              <p className="text-sm text-gray-600">
                Access verified B2B leads and post demand profiles.
              </p>
            </div>
            <div className="text-right">
              <div className="font-heading text-3xl font-semibold text-gray-900">$99</div>
              <div className="text-xs text-gray-500 font-mono uppercase tracking-wider">
                per month
              </div>
            </div>
          </div>

          <ul className="space-y-3 mb-8">
            <PlanRow bold="Purchase verified leads" tail=" — full marketplace access." />
            <PlanRow bold="Create demand profiles" tail=" — tell sellers what you need." />
            <PlanRow bold="Full Lead Passport visibility" tail=" — see verification history." />
            <PlanRow bold="Transaction protection" tail=" — 48-hour dispute window." />
            <PlanRow bold="Priority support" tail=" — direct line to our team." />
          </ul>

          <div className="flex flex-wrap gap-3">
            {hasActiveMembership ? (
              <Button
                onClick={handleManageBilling}
                size="lg"
                variant="outline"
                data-testid="manage-billing-btn"
              >
                <CreditCard className="mr-2 h-4 w-4" /> Manage billing
              </Button>
            ) : (
              <Button
                onClick={handleSubscribe}
                disabled={checkingOut}
                size="lg"
                className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
                data-testid="subscribe-btn"
              >
                {checkingOut ? 'Redirecting…' : 'Subscribe Now — $99/month'}
              </Button>
            )}
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/marketplace')}
              data-testid="browse-marketplace-btn"
            >
              Browse marketplace
            </Button>
          </div>
        </div>

        {/* FAQ */}
        <div className="border border-gray-200 rounded-lg p-6">
          <h3 className="font-heading text-lg font-semibold text-gray-900 mb-5 flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#0055FF]" /> Frequently asked questions
          </h3>

          <div className="space-y-5">
            <FaqRow
              q="What's included in the membership?"
              a="The $99/month membership gives you full access to purchase leads from the marketplace, create demand profiles, and use all buyer tools. Individual leads are charged separately at seller-set prices."
            />
            <FaqRow
              q="Can I cancel anytime?"
              a="Yes. Cancel any time — you keep access until the end of your current billing period. Leads you've already purchased remain permanently accessible."
            />
            <FaqRow
              q="Are lead costs separate?"
              a="Yes. The $99 is an access fee. Each lead has its own price set by the seller. You'll always see the exact cost before purchasing."
            />
          </div>
        </div>
      </div>
    </div>
  );
}

const MetaItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3">
    <div className="h-10 w-10 rounded-md bg-blue-50 flex items-center justify-center flex-shrink-0">
      <Icon className="h-4 w-4 text-[#0055FF]" />
    </div>
    <div>
      <div className="text-xs uppercase tracking-wider text-gray-500 font-mono">{label}</div>
      <div className="text-sm font-semibold text-gray-900">{value}</div>
    </div>
  </div>
);

const PlanRow = ({ bold, tail }) => (
  <li className="flex items-start gap-3 text-sm text-gray-700">
    <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
    <span>
      <strong className="text-gray-900">{bold}</strong>
      {tail}
    </span>
  </li>
);

const FaqRow = ({ q, a }) => (
  <div>
    <div className="font-semibold text-sm text-gray-900 mb-1">{q}</div>
    <div className="text-sm text-gray-600 leading-relaxed">{a}</div>
  </div>
);
